import axios, { AxiosError, AxiosResponse } from 'axios'
import { config } from '@/config'
import { TCSAPIResponse } from '@/types/types'
import { formatResponse } from './helpers/formatResponse'
import { DashboardItem } from '@/types/dashboard'
import type {
  ChatSessionResponse,
  SendChatMessageResponse,
  ConfirmPanelsResponse,
  GetPanelsResponse,
  ProposedPanel,
  TriggerSyncResponse,
  GetDashboardDataResponse,
  GetPanelDataResponse,
} from '@/types/Dashboards.types'
import type {
  DomainExecutionResultResponse,
  GetDashboardDataV1Response,
} from '@/types/DashboardV1.types'
import type { PanelConfig } from '@/types/Dashboards.types'

// ─── /build endpoint types ────────────────────────────────────────────────────

export interface BuildDashboardRequest {
  confirmedPanelIds: string[]
  idempotencyKey?: string
}

export interface BuiltPanel {
  aiInsight: null | string
  config: PanelConfig
  id: string
  order: number
  panelType: string
  syncRequired: boolean
  title: string
}

export interface BuildDashboardResponse {
  flowState: 'complete'
  panels: BuiltPanel[]
}

export const DashboardsAPI = {
  getAll: async (token: string) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/dashboards`,
        headers: {
          Authorization: `Bearer ${token}`
        },
      })

      return formatResponse<DashboardItem[]>(resp.data, null)
    } catch (error) {
      return formatResponse<DashboardItem[]>(null, error as AxiosError)
    }
  },

  getById: async (token: string, dashboardId: string) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}`,
        headers: {
          Authorization: `Bearer ${token}`
        },
      })

      return formatResponse<DashboardItem>(resp.data, null)
    } catch (error) {
      return formatResponse<DashboardItem>(null, error as AxiosError)
    }
  },

  create: async (token: string, body: { name: string }) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: body
      })

      return formatResponse<DashboardItem>(resp.data, null)
    } catch (error) {
      return formatResponse<DashboardItem>(null, error as AxiosError)
    }
  },

  rename: async (token: string, dashboardId: string, body: { name: string }) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'PATCH',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: body
      })

      return formatResponse<DashboardItem>(resp.data, null)
    } catch (error) {
      return formatResponse<DashboardItem>(null, error as AxiosError)
    }
  },

  delete: async (token: string, dashboardId: string) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'DELETE',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}`,
        headers: {
          Authorization: `Bearer ${token}`
        },
      })

      // DELETE returns 204 No Content — no response body to parse.
      if (resp.status === 204) {
        return { data: {} as Record<string, never>, err: undefined }
      }

      return formatResponse<Record<string, never>>(resp.data, null)
    } catch (error) {
      return formatResponse<Record<string, never>>(null, error as AxiosError)
    }
  },

  getChatSession: async (token: string, dashboardId: string) => {
    try {
      const resp: AxiosResponse<ChatSessionResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}/chat`,
        headers: {
          Authorization: `Bearer ${token}`
        },
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      return formatResponse<ChatSessionResponse>(null, error as AxiosError)
    }
  },

  sendChatMessage: async (token: string, dashboardId: string, message: string) => {
    try {
      const resp: AxiosResponse<SendChatMessageResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}/chat`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { message },
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      return formatResponse<SendChatMessageResponse>(null, error as AxiosError)
    }
  },

  confirmPanels: async (token: string, dashboardId: string, panels: ProposedPanel[]) => {
    try {
      const resp: AxiosResponse<ConfirmPanelsResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}/panels`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { panels },
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      return formatResponse<ConfirmPanelsResponse>(null, error as AxiosError)
    }
  },

  getDashboardPanels: async (token: string, dashboardId: string) => {
    try {
      const resp: AxiosResponse<GetPanelsResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}/panels`,
        headers: {
          Authorization: `Bearer ${token}`
        },
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      return formatResponse<GetPanelsResponse>(null, error as AxiosError)
    }
  },

  triggerSync: async (token: string, dashboardId: string, options?: { forceRefresh?: boolean }) => {
    try {
      const resp: AxiosResponse<TriggerSyncResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}/sync`,
        headers: { Authorization: `Bearer ${token}` },
        data: { forceRefresh: options?.forceRefresh ?? false },
      })
      return { data: resp.data, err: undefined }
    } catch (error) {
      return formatResponse<TriggerSyncResponse>(null, error as AxiosError)
    }
  },

  getDashboardData: async (token: string, dashboardId: string) => {
    try {
      const resp: AxiosResponse<GetDashboardDataResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}/data`,
        headers: { Authorization: `Bearer ${token}` },
      })
      return { data: resp.data, err: undefined }
    } catch (error) {
      return formatResponse<GetDashboardDataResponse>(null, error as AxiosError)
    }
  },

  getPanelData: async (token: string, dashboardId: string, panelId: string) => {
    try {
      const resp: AxiosResponse<GetPanelDataResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}/panels/${panelId}/data`,
        headers: { Authorization: `Bearer ${token}` },
      })
      return { data: resp.data, err: undefined }
    } catch (error) {
      return formatResponse<GetPanelDataResponse>(null, error as AxiosError)
    }
  },

  // ── v1 Dashboard sync endpoints (§3d.8) ────────────────────────────────────

  /**
   * GET /v1/dashboards/:id/data  (v1 typed response)
   *
   * Returns per-panel display data for all panels on the dashboard.
   * Used by PanelCanvasV1 via `loadDashboardDataV1Thunk` after initial
   * build or after a sync/retry cycle completes.
   *
   * This is a typed wrapper around the same endpoint as `getDashboardData`
   * but returns the v1 response shape (`GetDashboardDataV1Response`) that
   * includes `PanelDataItemV1` items with typed `displayData`.
   */
  getDashboardDataV1: async (
    token: string,
    dashboardId: string
  ): Promise<{ data: GetDashboardDataV1Response | undefined; err: string | undefined; status: number | undefined }> => {
    try {
      const resp: AxiosResponse<GetDashboardDataV1Response> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/dashboards/${dashboardId}/data`,
        headers: { Authorization: `Bearer ${token}` },
      })
      return { data: resp.data, err: undefined, status: resp.status }
    } catch (error) {
      const axiosErr = error as AxiosError
      const formatted = formatResponse<GetDashboardDataV1Response>(null, axiosErr)
      return { ...formatted, status: axiosErr.response?.status }
    }
  },

  /**
   * POST /v1/dashboards/:id/sync
   * Default incremental sync. Target of the Sync button.
   *
   * Delegates to `triggerSync` with `forceRefresh: false` so there is a single
   * underlying HTTP call site for this endpoint.
   *
   * TODO consolidate v1 dashboard sync surface — see PR #315 review
   */
  syncDashboard: async (
    token: string,
    id: string
  ): Promise<{ data: TriggerSyncResponse | undefined; err: string | undefined }> => {
    return DashboardsAPI.triggerSync(token, id, { forceRefresh: false })
  },

  /**
   * POST /v1/dashboards/:id/sync  (with `forceRefresh: true`)
   *
   * Full reset sync. Confirmation-gated from UI. There is NO dedicated
   * `/sync/reset` endpoint on the BE — reset is a flag on the single sync
   * endpoint. `forceRefresh: true` maps to `syncMode: 'reset'` server-side,
   * which clears ALL per-(dashboard, domain) checkpoint rows (including
   * per-branch fan-out hashes — see tcs-api commit on
   * dashboard-sync-checkpoint.repository) and deletes cached panel rows
   * before re-fetching from CampMinder.
   *
   * The `opts.domainIds` parameter is currently unused — the BE applies
   * reset to every domain on the dashboard. Retained for future
   * domain-scoped reset support.
   */
  resetDashboard: async (
    token: string,
    id: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- retained for future domain-scoped reset support
    _opts?: { domainIds?: string[] }
  ): Promise<{ data: TriggerSyncResponse | undefined; err: string | undefined }> => {
    try {
      const resp: AxiosResponse<TriggerSyncResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${id}/sync`,
        headers: { Authorization: `Bearer ${token}` },
        data: { forceRefresh: true },
      })
      // /v1/dashboards/:id/sync returns the raw TriggerSyncResponse shape
      // (`{ alreadySyncing: boolean }`) — NOT the legacy `{ success, data }`
      // envelope. Return it directly per the v1 raw-shape pattern, mirroring
      // the neighbour `triggerSync` (Dashboards.ts ~L207). Routing through
      // formatResponse here was incorrect — formatResponse requires
      // `success: true` on the envelope and otherwise reports
      // `err: 'Unable to fetch from API'` even on HTTP 200, causing
      // resetDashboardThunk to treat every successful reset as a failure.
      return { data: resp.data, err: undefined }
    } catch (error) {
      return formatResponse<TriggerSyncResponse>(null, error as AxiosError)
    }
  },

  /**
   * POST /v1/dashboards/:id/build
   *
   * Deterministic build endpoint (Slice 1.5 of the deterministic-panels plan).
   * Materialises panel `config` from `PanelProposalHint.suggestedTransform` +
   * `flow_context` server-side — the FE only sends panel IDs.
   *
   * Success: 201, raw shape `{ flowState, panels }` — NOT wrapped in the
   * legacy `TCSAPIResponse` envelope (CEO 2026-04-28 directive). We return
   * `resp.data` directly on success.
   *
   * Error: `response.data.error` contains a machine-readable code:
   *   409 INVALID_FSM_TRANSITION
   *   400 NO_PANELS_CONFIRMED | UNKNOWN_PANEL_ID | UNSUPPORTED_DOMAIN | MISSING_CREDENTIAL
   *   404 SESSION_NOT_FOUND
   *   500 INTERNAL_ERROR
   * The `errorCode` field in the return shape surfaces this code for callers
   * to branch on.
   *
   * The legacy `confirmPanels` (POST /panels) is intentionally preserved —
   * it may be used by AI-authored flows or canvas-era code paths.
   */
  buildDashboard: async (
    token: string,
    dashboardId: string,
    body: BuildDashboardRequest
  ): Promise<{
    data: BuildDashboardResponse | undefined
    err: string | undefined
    status: number | undefined
    errorCode: string | undefined
    /** domainId from a 400 UNSUPPORTED_DOMAIN error body — undefined otherwise. */
    errorDomainId: string | undefined
    /**
     * actualState from a 409 INVALID_FSM_TRANSITION body — the flow_state the
     * BE *actually* sees on the session. Surfaced so the FE can render
     * step-specific copy instead of unconditionally saying "Dashboard already
     * built" for every 409 (Bug #4, 2026-05-14 bugbash).
     *
     * Possible values: 'awaiting_source' | 'awaiting_credential' |
     * 'awaiting_domain' | 'awaiting_filters' | 'syncing' | 'complete' OR an
     * `edit:<state>` prefix for edit-mode sessions. Undefined for non-409.
     */
    errorActualState?: string
  }> => {
    try {
      const reqBody: BuildDashboardRequest = {
        confirmedPanelIds: body.confirmedPanelIds,
      }
      if (body.idempotencyKey) {
        reqBody.idempotencyKey = body.idempotencyKey
      }
      const resp: AxiosResponse<BuildDashboardResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${encodeURIComponent(dashboardId)}/build`,
        headers: { Authorization: `Bearer ${token}` },
        data: reqBody,
      })
      // Raw shape — no envelope unwrap. resp.data IS the BuildDashboardResponse.
      return {
        data: resp.data,
        err: undefined,
        status: resp.status,
        errorCode: undefined,
        errorDomainId: undefined,
      }
    } catch (error) {
      const axiosErr = error as AxiosError<{
        error?: string
        reason?: string
        domainId?: string
        // Bug #4 (2026-05-14): BE 409 carries the actual flow_state under
        // `actualState` (see dashboard-build.controller.ts translateBuildFsmError).
        actualState?: string
      }>
      const status = axiosErr.response?.status
      const errBody = axiosErr.response?.data
      const errorCode = errBody?.error ?? errBody?.reason
      // domainId is present on UNSUPPORTED_DOMAIN errors — surface it for targeted UI messaging.
      const errorDomainId = errBody?.domainId
      // actualState is present on INVALID_FSM_TRANSITION errors — surface it so the FE
      // can render step-specific copy instead of conflating every 409 with "already built."
      const errorActualState =
        typeof errBody?.actualState === 'string' ? errBody.actualState : undefined
      const formatted = formatResponse<BuildDashboardResponse>(null, axiosErr)
      return {
        data: formatted.data,
        err: formatted.err,
        status,
        errorCode,
        errorDomainId,
        errorActualState,
      }
    }
  },

  /**
   * POST /v1/dashboards/:id/sync/domain/:domainId
   * Single-domain retry. Target of the per-domain error banner Retry button.
   */
  retryDomain: async (
    token: string,
    id: string,
    domainId: string
  ): Promise<{
    data: DomainExecutionResultResponse | undefined
    err: string | undefined
  }> => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${id}/sync/domain/${domainId}`,
        headers: { Authorization: `Bearer ${token}` },
        data: {},
      })
      return formatResponse<DomainExecutionResultResponse>(resp.data, null)
    } catch (error) {
      return formatResponse<DomainExecutionResultResponse>(null, error as AxiosError)
    }
  },
}
